import { useEffect, useRef } from "react";

/**
 * Canvas + video fallback when Screen Wake Lock is unavailable or denied (common on iOS Safari).
 * Plays a 1×1 silent stream so the device stays awake during training.
 */
function startCanvasKeepAwake() {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  if (!canvas.captureStream) return null;

  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d");
  let frame = 0;
  const tick = () => {
    if (!ctx) return;
    ctx.fillStyle = frame++ % 2 ? "#000" : "#001";
    ctx.fillRect(0, 0, 1, 1);
  };
  tick();
  const interval = window.setInterval(tick, 1000);

  const stream = canvas.captureStream(1);
  const video = document.createElement("video");
  video.setAttribute("playsinline", "true");
  video.setAttribute("webkit-playsinline", "true");
  video.muted = true;
  video.autoplay = true;
  video.loop = true;
  video.srcObject = stream;
  video.style.cssText =
    "position:fixed;opacity:0;pointer-events:none;width:1px;height:1px;z-index:-1";
  document.body.appendChild(video);

  const play = () => {
    video.play().catch(() => {});
  };

  return {
    play,
    stop: () => {
      window.clearInterval(interval);
      try {
        video.pause();
        video.srcObject = null;
        video.remove();
      } catch {
        /* ignore */
      }
      stream.getTracks().forEach((t) => t.stop());
    },
  };
}

/**
 * Keeps the screen awake using the Screen Wake Lock API.
 * Re-acquires on user gesture, visibility changes, and when the lock is released.
 * Falls back to a silent video stream on iOS / when wake lock is denied.
 */
export function useScreenWakeLock(enabled = true) {
  const wakeLockRef = useRef(null);
  const fallbackRef = useRef(null);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof navigator === "undefined") {
      return undefined;
    }

    let active = true;
    const hasWakeLockApi = "wakeLock" in navigator;

    async function releaseLock() {
      if (!wakeLockRef.current) return;
      try {
        await wakeLockRef.current.release();
      } catch {
        // already released
      }
      wakeLockRef.current = null;
    }

    function stopFallback() {
      fallbackRef.current?.stop?.();
      fallbackRef.current = null;
    }

    function ensureFallback() {
      if (fallbackRef.current) return;
      const fb = startCanvasKeepAwake();
      if (fb) {
        fallbackRef.current = fb;
        fb.play();
      }
    }

    async function requestLock() {
      if (!active || !enabledRef.current) return;
      if (document.visibilityState !== "visible") return;

      if (hasWakeLockApi) {
        if (wakeLockRef.current) return;
        try {
          const lock = await navigator.wakeLock.request("screen");
          if (!active) {
            await lock.release();
            return;
          }
          wakeLockRef.current = lock;
          lock.addEventListener("release", () => {
            if (wakeLockRef.current === lock) {
              wakeLockRef.current = null;
            }
            if (active && enabledRef.current) {
              requestLock();
              ensureFallback();
            }
          });
          stopFallback();
          return;
        } catch {
          ensureFallback();
        }
      } else {
        ensureFallback();
      }
    }

    const onUserGesture = () => {
      requestLock();
      fallbackRef.current?.play?.();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestLock();
      } else {
        releaseLock();
      }
    };

    const onPageShow = () => {
      requestLock();
    };

    requestLock();

    const gestureOpts = { passive: true };
    document.addEventListener("pointerdown", onUserGesture, gestureOpts);
    document.addEventListener("touchstart", onUserGesture, gestureOpts);
    document.addEventListener("click", onUserGesture, gestureOpts);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onUserGesture);
    window.addEventListener("resize", onUserGesture);
    window.addEventListener("orientationchange", onUserGesture);

    return () => {
      active = false;
      document.removeEventListener("pointerdown", onUserGesture);
      document.removeEventListener("touchstart", onUserGesture);
      document.removeEventListener("click", onUserGesture);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onUserGesture);
      window.removeEventListener("resize", onUserGesture);
      window.removeEventListener("orientationchange", onUserGesture);
      releaseLock();
      stopFallback();
    };
  }, [enabled]);
}

export default useScreenWakeLock;
